/**
 * Sample Selection Modal
 * 
 * PURPOSE: Allow authors to select 1-3 chapters as free samples for guest preview.
 * 
 * CRITICAL: This feature creates ZERO new content. It only creates a pointer document
 * referencing existing segments. No TTS calls, no audio generation, no R2 writes.
 * 
 * DATA SOURCE: Uses the SAME outline endpoint as the manuscript player for chapter data.
 * - V3 manuscripts: GET /api/v3/manuscripts/:id/outline → segments[]
 * - Simple Ingest/V1/V2: GET /api/manuscripts/:id/outline → chapters[]
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Loader2, Music, Clock, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_URL } from '@/config/api';

const API = `${BACKEND_URL}/api`;

const SampleSelectionModal = ({ 
  isOpen, 
  onClose, 
  manuscriptId, 
  onSampleCreated,
  hasExistingSample = false 
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [existingSample, setExistingSample] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [manuscriptTitle, setManuscriptTitle] = useState('');

  // Fetch chapters when modal opens - uses SAME endpoint as manuscript player
  useEffect(() => {
    if (isOpen && manuscriptId) {
      fetchChapters();
    }
  }, [isOpen, manuscriptId]);

  const fetchChapters = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      // First, get manuscript to determine type (same check as ManuscriptPlayer)
      const manuscriptRes = await axios.get(`${API}/manuscripts/${manuscriptId}`, { headers });
      const manuscriptData = manuscriptRes.data;
      
      // Store manuscript title for display
      setManuscriptTitle(manuscriptData.title || 'Untitled');
      
      // Detect manuscript type - SAME logic as ManuscriptPlayer lines 1488-1550
      const isV3 = manuscriptData.ingestion_version === 3;
      const isSimpleIngest = manuscriptData.simple_ingest_job_id != null;
      
      let chaptersData = [];
      
      if (isV3) {
        // V3: Use v3 outline endpoint which returns segments
        const outlineRes = await axios.get(`${API}/v3/manuscripts/${manuscriptId}/outline`, { headers });
        const outlineData = outlineRes.data;
        
        // Map segments to chapters format - SAME as ManuscriptPlayer lines 1513-1521
        chaptersData = (outlineData.segments || []).map((seg, idx) => ({
          segment_id: seg.id,
          segment_index: idx,
          title: seg.label || `Segment ${idx + 1}`,
          order: seg.order,
          audio_order: seg.audio_order
        }));
        
        console.log('[SampleModal] V3 manuscript loaded with', chaptersData.length, 'segments');
      } else {
        // Simple Ingest / V1/V2: Use standard outline endpoint
        const outlineRes = await axios.get(`${API}/manuscripts/${manuscriptId}/outline`, { headers });
        const outlineData = outlineRes.data;
        
        // Map chapters - SAME as ManuscriptPlayer lines 1530-1536
        chaptersData = (outlineData.chapters || []).map((ch, idx) => ({
          segment_id: ch.segment_id || ch.id || `chapter-${idx}`,
          segment_index: idx,
          title: ch.label || ch.title || `Chapter ${idx + 1}`,
          word_count: ch.word_count || 0
        }));
        
        console.log('[SampleModal] Standard manuscript loaded with', chaptersData.length, 'chapters');
        
        // FALLBACK: If only 1 generic chapter, try V3 endpoint which reads from segments collection
        // This handles edge cases where outline field isn't populated but segments exist
        if (chaptersData.length === 1 && chaptersData[0].title === 'Full Manuscript') {
          try {
            const v3OutlineRes = await axios.get(`${API}/v3/manuscripts/${manuscriptId}/outline`, { headers });
            const v3Data = v3OutlineRes.data;
            if (v3Data.segments && v3Data.segments.length > 1) {
              chaptersData = v3Data.segments.map((seg, idx) => ({
                segment_id: seg.id,
                segment_index: idx,
                title: seg.label || `Segment ${idx + 1}`,
                order: seg.order,
                audio_order: seg.audio_order
              }));
              console.log('[SampleModal] Fallback: Loaded', chaptersData.length, 'segments from v3 endpoint');
            }
          } catch (v3Err) {
            console.log('[SampleModal] V3 fallback not available, using outline chapters');
          }
        }
      }
      
      setChapters(chaptersData);
      
      // Check for existing sample selection from manuscript data
      if (manuscriptData.hasSample && manuscriptData.sampleManifest?.sample_segments) {
        const existingIds = manuscriptData.sampleManifest.sample_segments.map(s => s.segment_id);
        setSelectedIds(existingIds);
        setExistingSample(true);
      } else {
        setSelectedIds([]);
        setExistingSample(hasExistingSample);
      }
      
    } catch (error) {
      console.error('[SampleModal] Error fetching chapters:', error);
      toast.error('Failed to load chapters');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChapter = (segmentId) => {
    setSelectedIds(prev => {
      if (prev.includes(segmentId)) {
        // Remove from selection
        return prev.filter(id => id !== segmentId);
      } else {
        // Add to selection (max 3)
        if (prev.length >= 3) {
          toast.warning('You can select up to 3 chapters. Deselect one to choose a different chapter.');
          return prev;
        }
        return [...prev, segmentId];
      }
    });
  };

  const handleSave = async () => {
    if (selectedIds.length < 1) {
      toast.error('Please select at least 1 chapter');
      return;
    }
    if (selectedIds.length > 3) {
      toast.error('You can select up to 3 chapters');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const method = existingSample ? 'put' : 'post';
      
      await axios[method](
        `${API}/manuscripts/${manuscriptId}/sample`,
        { segment_ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(existingSample ? 'Sample updated successfully' : 'Sample created successfully');
      onSampleCreated?.();
      onClose();
    } catch (error) {
      console.error('[SampleModal] Error saving sample:', error);
      toast.error(error.response?.data?.detail || 'Failed to save sample');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API}/manuscripts/${manuscriptId}/sample`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Sample removed. Guests can no longer preview this title.');
      setShowDeleteConfirm(false);
      onSampleCreated?.();
      onClose();
    } catch (error) {
      console.error('[SampleModal] Error deleting sample:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove sample');
    } finally {
      setDeleting(false);
    }
  };

  // Calculate total duration of selected chapters (if duration available)
  const totalDuration = chapters
    .filter(ch => selectedIds.includes(ch.segment_id))
    .reduce((sum, ch) => sum + (ch.duration || 0), 0);

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Music className="w-5 h-5 text-purple-400" />
              {existingSample ? 'Edit Sample' : 'Select Sample Chapters'}
            </DialogTitle>
            {/* Book Title - Prominently displayed */}
            {manuscriptTitle && (
              <div className="mt-2 mb-1">
                <span className="text-lg font-semibold text-white" data-testid="sample-modal-book-title">
                  {manuscriptTitle}
                </span>
              </div>
            )}
            <DialogDescription className="text-slate-400">
              Choose 1 to 3 chapters for guests to preview for free.
              {existingSample && ' Update your selection or remove the sample entirely.'}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : chapters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <AlertCircle className="w-10 h-10 mb-3" />
              <p>No chapters available</p>
              <p className="text-sm mt-1">Publish your audiobook first to create a sample.</p>
            </div>
          ) : (
            <>
              {/* Selection Summary */}
              <div className="flex items-center justify-between px-1 py-2 bg-slate-800/50 rounded-lg mb-3">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-300">
                    <span className="font-bold text-purple-400">{selectedIds.length}</span> of 3 chapters selected
                  </span>
                  {selectedIds.length > 0 && totalDuration > 0 && (
                    <span className="text-sm text-slate-400 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {formatDuration(totalDuration)} total
                    </span>
                  )}
                </div>
                {selectedIds.length >= 3 && (
                  <span className="text-xs text-amber-400">Maximum reached</span>
                )}
              </div>

              {/* Chapter List */}
              <div className="flex-1 overflow-y-auto space-y-2 pr-2 max-h-[40vh]" data-testid="chapter-list">
                {chapters.map((chapter, idx) => {
                  const isSelected = selectedIds.includes(chapter.segment_id);
                  const isDisabled = !isSelected && selectedIds.length >= 3;
                  const durationStr = formatDuration(chapter.duration);
                  
                  return (
                    <div
                      key={chapter.segment_id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                        isSelected 
                          ? 'bg-purple-900/30 border-purple-500/50' 
                          : isDisabled
                            ? 'bg-slate-800/30 border-slate-700/50 opacity-50 cursor-not-allowed'
                            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                      }`}
                      onClick={() => !isDisabled && handleToggleChapter(chapter.segment_id)}
                      data-testid={`chapter-row-${idx}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onCheckedChange={() => handleToggleChapter(chapter.segment_id)}
                        className="data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-200 truncate">
                          {chapter.title}
                        </p>
                        {durationStr && (
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {durationStr}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <span className="text-xs text-slate-500 flex-shrink-0">
                        #{chapter.segment_index + 1}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Validation Message */}
              {selectedIds.length === 0 && (
                <p className="text-sm text-amber-400 mt-2">
                  Select at least 1 chapter to create a sample.
                </p>
              )}
              
              {/* Informational Note about Sample Credits */}
              <div className="flex items-start gap-3 mt-4 p-4 bg-amber-900/20 rounded-lg border border-amber-600/30">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-bold text-amber-400 leading-relaxed">
                  Sample chapters are for marketing and product familiarization only. Reader credits are not accrued and commissions are not generated from sample plays.
                </p>
              </div>
            </>
          )}

          <DialogFooter className="flex-shrink-0 mt-4 gap-2">
            {/* Remove Sample Button (only if editing) */}
            {existingSample && (
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="mr-auto border-red-500/50 text-red-400 hover:bg-red-500/10"
                disabled={saving || deleting}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Remove Sample
              </Button>
            )}
            
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving || deleting}
              className="border-slate-600"
            >
              Cancel
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={saving || deleting || selectedIds.length < 1 || selectedIds.length > 3 || loading}
              className="bg-purple-600 hover:bg-purple-500"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                existingSample ? 'Update Sample' : 'Save Sample'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-400">
              Remove Sample?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Guests will no longer be able to preview this title. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Sample'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SampleSelectionModal;
